import React, { useState, useEffect } from "react";
import EmailReportGeneratorAPI from "../services/EmailReportGeneratorAPI";
import { getCookie } from "../utils/CookieUtil";
import { formatDateTime } from "../utils/DateTimeUtil";
import DateRangeField from "../../common/datetime/DateRangeField";
import {
  browserTimeZone,
  instantToWallClock,
  wallClockToIso,
} from "../../common/datetime/dateTimeParts";
import "./css/EmailReportGenerator.css";
import LoadingSpinner from "../common/LoadingSpinner";

/** The range is picked in the viewer's own zone. */
const TZ = browserTimeZone();

function EmailReportGenerator({ selectedUser = null }) {
  console.log(
    "EmailReportGenerator: Component initialized with selectedUser:",
    selectedUser,
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [targetUuid, setTargetUuid] = useState("");
  const [reportMode, setReportMode] = useState("current-user"); // current-user, selected-user, specific-user, all-users
  const [reportResults, setReportResults] = useState(null);
  const [days, setDays] = useState(7);
  // Set default date range (current month) and get current user
  useEffect(() => {
    console.log(
      "EmailReportGenerator: useEffect - Setting default date range and getting current user",
    );

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0,
      23,
      59,
      59,
    );

    const startDateString = instantToWallClock(firstDayOfMonth, TZ);
    const endDateString = instantToWallClock(lastDayOfMonth, TZ);

    console.log(
      "EmailReportGenerator: Setting date range - Start:",
      startDateString,
      "End:",
      endDateString,
    );
    setStartDate(startDateString); // Format: YYYY-MM-DDTHH:mm
    setEndDate(endDateString);

    // Get current user ID from cookies
    const currentUuid = getCookie("user");
    console.log(
      "EmailReportGenerator: Current user from cookies:",
      currentUuid,
    );
    if (currentUuid) {
      setTargetUuid(currentUuid);
      console.log("EmailReportGenerator: Set targetUUuid to:", currentUuid);
    } else {
      console.log("EmailReportGenerator: No current user found in cookies");
    }
  }, []);

  // Update target user when selectedUser prop changes
  useEffect(() => {
    console.log(
      "EmailReportGenerator: useEffect - selectedUser changed:",
      selectedUser,
    );
    if (selectedUser && selectedUser.uuid) {
      const uuid = selectedUser.uuid;
      console.log(
        "EmailReportGenerator: Setting targetUUuid to selectedUser:",
        uuid,
      );
      setTargetUuid(uuid);
      setReportMode("selected-user");
      console.log(
        "EmailReportGenerator: Changed reportMode to 'selected-user'",
      );
    }
  }, [selectedUser]);

  const handleGenerateReports = async () => {
    console.log("EmailReportGenerator: handleGenerateReports called");
    console.log(
      "EmailReportGenerator: Current state - startDate:",
      startDate,
      "endDate:",
      endDate,
      "reportMode:",
      reportMode,
      "targetUuid:",
      targetUuid,
    );

    if (!startDate || !endDate) {
      console.log("EmailReportGenerator: Error - Missing start or end date");
      setError("Please provide both start date and end date");
      return;
    }

    if (reportMode !== "all-users" && !targetUuid) {
      console.log(
        "EmailReportGenerator: Error - Missing user ID for non-all-users mode",
      );
      setError("Please provide a user ID");
      return;
    }

    console.log("EmailReportGenerator: Starting report generation...");
    setLoading(true);
    setError(null);
    setSuccess(null);
    setReportResults(null);

    try {
      const startDateTime = wallClockToIso(startDate, TZ);
      const endDateTime = wallClockToIso(endDate, TZ);
      console.log(
        "EmailReportGenerator: Converted dates - Start:",
        startDateTime,
        "End:",
        endDateTime,
      );

      let response;

      switch (reportMode) {
        case "all-users":
          console.log(
            "EmailReportGenerator: Calling generateReportsForAllUsers",
          );
          response = await EmailReportGeneratorAPI.generateReportsForAllUsers(
            startDateTime,
            endDateTime,
          );
          break;
        default:
          console.log(
            "EmailReportGenerator: Calling generateReportsForUser with uuid:",
            targetUuid,
          );
          response = await EmailReportGeneratorAPI.generateReportsForUser(
            targetUuid,
            startDateTime,
            endDateTime,
          );
          break;
      }

      console.log("EmailReportGenerator: API response received:", response);
      setReportResults(response.data);
      setSuccess(response.message || "Email reports generated successfully!");

      console.log("Email reports generated:", response);
    } catch (err) {
      console.error(
        "EmailReportGenerator: Error generating email reports:",
        err,
      );
      setError(err.message || "Failed to generate email reports");
    } finally {
      console.log(
        "EmailReportGenerator: Report generation completed, setting loading to false",
      );
      setLoading(false);
    }
  };

  const handleGenerateSimpleReports = async () => {
    console.log("EmailReportGenerator: handleGenerateSimpleReports called");
    console.log(
      "EmailReportGenerator: Current state - targetUuid:",
      targetUuid,
      "days:",
      days,
    );

    if (!targetUuid) {
      console.log(
        "EmailReportGenerator: Error - Missing user ID for simple reports",
      );
      setError("Please provide a user ID");
      return;
    }

    console.log("EmailReportGenerator: Starting simple report generation...");
    setLoading(true);
    setError(null);
    setSuccess(null);
    setReportResults(null);

    try {
      console.log(
        "EmailReportGenerator: Calling generateReportsForUserSimple with uuid:",
        targetUuid,
        "days:",
        days,
      );
      const response =
        await EmailReportGeneratorAPI.generateReportsForUserSimple(
          targetUuid,
          days,
        );

      console.log(
        "EmailReportGenerator: Simple API response received:",
        response,
      );
      setReportResults(response.data);
      setSuccess(
        response.message || `Email reports generated for last ${days} days!`,
      );

      console.log("Simple email reports generated:", response);
    } catch (err) {
      console.error(
        "EmailReportGenerator: Error generating simple email reports:",
        err,
      );
      setError(err.message || "Failed to generate email reports");
    } finally {
      console.log(
        "EmailReportGenerator: Simple report generation completed, setting loading to false",
      );
      setLoading(false);
    }
  };

  const handleSynchronizeReports = async () => {
    console.log("EmailReportGenerator: handleSynchronizeReports called");
    console.log(
      "EmailReportGenerator: Current state - startDate:",
      startDate,
      "endDate:",
      endDate,
    );

    if (!startDate || !endDate) {
      console.log(
        "EmailReportGenerator: Error - Missing start or end date for synchronization",
      );
      setError(
        "Please provide both start date and end date for synchronization",
      );
      return;
    }

    console.log("EmailReportGenerator: Starting synchronization...");
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const startDateTime = wallClockToIso(startDate, TZ);
      const endDateTime = wallClockToIso(endDate, TZ);
      console.log(
        "EmailReportGenerator: Sync dates - Start:",
        startDateTime,
        "End:",
        endDateTime,
      );

      console.log("EmailReportGenerator: Calling synchronizeEmailReports");
      const response = await EmailReportGeneratorAPI.synchronizeEmailReports(
        startDateTime,
        endDateTime,
      );

      console.log("EmailReportGenerator: Sync response received:", response);
      setSuccess(
        response.message || "Email reports synchronized successfully!",
      );

      console.log("Email reports synchronized:", response);
    } catch (err) {
      console.error(
        "EmailReportGenerator: Error synchronizing email reports:",
        err,
      );
      setError(err.message || "Failed to synchronize email reports");
    } finally {
      console.log(
        "EmailReportGenerator: Synchronization completed, setting loading to false",
      );
      setLoading(false);
    }
  };
  const clearResults = () => {
    console.log("EmailReportGenerator: clearResults called");
    setReportResults(null);
    setError(null);
    setSuccess(null);
  };
  const getUserDisplayName = () => {
    if (selectedUser) {
      const displayName = `${selectedUser.firstName} ${selectedUser.lastName} (ID: ${selectedUser.uuid})`;
      console.log(
        "EmailReportGenerator: getUserDisplayName - selectedUser display:",
        displayName,
      );
      return displayName;
    }
    const fallbackName = " (Select a user to run email report)";
    console.log(
      "EmailReportGenerator: getUserDisplayName - no user selected:",
      fallbackName,
    );
    return fallbackName;
  };

  return (
    <div className="email-report-generator">
      <div className="generator-header">
        <h2>Generate Email Reports</h2>
        <p>
          Generate email reports from SendGrid data for analysis and monitoring
        </p>
      </div>{" "}
      {/* Report Mode Selection */}
      <div className="form-section">
        <h3>Report Target</h3>
        <div className="radio-group">
          <label>
            {" "}
            <input
              type="radio"
              value="selected-user"
              checked={reportMode === "selected-user"}
              disabled={!selectedUser}
              onChange={(e) => {
                console.log(
                  "EmailReportGenerator: Report mode changed to:",
                  e.target.value,
                );
                setReportMode(e.target.value);
              }}
            />
            Selected User: {getUserDisplayName()}
          </label>
          <label>
            {" "}
            <input
              type="radio"
              value="specific-user"
              checked={reportMode === "specific-user"}
              onChange={(e) => {
                console.log(
                  "EmailReportGenerator: Report mode changed to:",
                  e.target.value,
                );
                setReportMode(e.target.value);
              }}
            />
            Specific User ID
          </label>
          <label>
            {" "}
            <input
              type="radio"
              value="all-users"
              checked={reportMode === "all-users"}
              onChange={(e) => {
                console.log(
                  "EmailReportGenerator: Report mode changed to:",
                  e.target.value,
                );
                setReportMode(e.target.value);
              }}
            />
            All Users
          </label>
        </div>
      </div>
      {/* User ID Input (when needed) */}
      {(reportMode === "specific-user" ||
        (reportMode === "current-user" && !getCookie("user"))) && (
        <div className="form-section">
          <label htmlFor="targetUuid">User ID:</label>
          <input
            type="number"
            id="targetUuid"
            value={targetUuid}
            onChange={(e) => {
              console.log(
                "EmailReportGenerator: TargetUuid changed to:",
                e.target.value,
              );
              setTargetUuid(e.target.value);
            }}
            placeholder="Enter user ID"
            min="1"
          />
        </div>
      )}{" "}
      {/* Generate Sections - Side by Side */}
      <div className="generate-sections-container">
        {/* Date Range Selection */}
        <div className="form-section generate-section-half">
          <h3>Generate by Date Range</h3>
          <DateRangeField
            mode="datetime"
            fromLabel="Start date"
            toLabel="End date"
            from={startDate}
            to={endDate}
            presets={["today", "7d", "30d", "mtd"]}
            onChange={({ from, to }) => {
              setStartDate(from);
              setEndDate(to);
            }}
          />
          <div className="date-range-button">
            <button
              onClick={handleGenerateReports}
              disabled={loading}
              className="btn-primary"
            >
              {loading ? "Generating..." : "Generate Reports"}
            </button>
          </div>
        </div>{" "}
        {/* Simple Generation (Days Back) */}
        {reportMode !== "all-users" && (
          <div className="form-section generate-section-half">
            <h3>Quick Generate (Last N Days)</h3>
            <div className="simple-generate">
              <div className="simple-generate-inputs">
                <label htmlFor="days">Days back from today:</label>
                <input
                  type="number"
                  id="days"
                  value={days}
                  onChange={(e) => {
                    const newDays = parseInt(e.target.value);
                    console.log(
                      "EmailReportGenerator: Days changed to:",
                      newDays,
                    );
                    setDays(newDays);
                  }}
                  min="1"
                  max="365"
                />
              </div>
              <div className="simple-generate-button">
                <button
                  onClick={handleGenerateSimpleReports}
                  disabled={loading}
                  className="btn-simple"
                >
                  {loading ? "Generating..." : `Generate Last ${days} Days`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>{" "}
      {/* Action Buttons */}
      <div className="form-section-buttons">
        <div className="action-buttons">
          <button
            onClick={handleSynchronizeReports}
            disabled={loading}
            className="btn-sync"
          >
            {loading ? "Synchronizing..." : "Sync with SendGrid"}
          </button>

          {(reportResults || error || success) && (
            <button onClick={clearResults} className="btn-clear">
              Clear Results
            </button>
          )}
        </div>
      </div>
      {/* Loading State */}
      {loading && (
        <LoadingSpinner message="Processing email report generation..." />
      )}
      {/* Error Display */}
      {error && (
        <div className="error-message">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}
      {/* Success Display */}
      {success && (
        <div className="success-message">
          <h4>Success</h4>
          <p>{success}</p>
        </div>
      )}
      {/* Results Display */}
      {reportResults && (
        <div className="results-section">
          <h3>Generation Results</h3>
          <div className="results-summary">
            {reportResults.uuid && (
              <div className="summary-item">
                <strong>User ID:</strong> {reportResults.uuid}
              </div>
            )}
            <div className="summary-item">
              <strong>Reports Generated:</strong>{" "}
              {reportResults.reportsGenerated || 0}
            </div>
            {reportResults.startDate && (
              <div className="summary-item">
                <strong>Start Date:</strong>{" "}
                {formatDateTime(reportResults.startDate)}
              </div>
            )}
            {reportResults.endDate && (
              <div className="summary-item">
                <strong>End Date:</strong>{" "}
                {formatDateTime(reportResults.endDate)}
              </div>
            )}
            {reportResults.daysBack && (
              <div className="summary-item">
                <strong>Days Back:</strong> {reportResults.daysBack}
              </div>
            )}
          </div>

          {reportResults.reports && reportResults.reports.length > 0 && (
            <div className="reports-list">
              <h4>Generated Reports Preview (First 5)</h4>
              <div className="reports-table">
                <div className="table-header">
                  <div>Message ID</div>
                  <div>Status</div>
                  <div>Timestamp</div>
                  <div>Event</div>
                </div>
                {reportResults.reports.slice(0, 5).map((report, index) => (
                  <div key={index} className="table-row">
                    <div>{report.messageId || "N/A"}</div>
                    <div>{report.status || "N/A"}</div>
                    <div>
                      {report.timestamp
                        ? formatDateTime(report.timestamp)
                        : "N/A"}
                    </div>
                    <div>{report.event || "N/A"}</div>
                  </div>
                ))}
              </div>
              {reportResults.reports.length > 5 && (
                <p>... and {reportResults.reports.length - 5} more reports</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EmailReportGenerator;
